import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Spinner,
  useToast,
  Flex,
  Badge,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { BACKEND_URL } from "../../../api/config";
import Confirmation from "../../../components/confirmation/Confirmation";

function IssuerMiddlemenList() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const toast = useToast();
  const abortRef = useRef(false);
  const errorShownRef = useRef(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingChange, setPendingChange] = useState(null);

  /* ---------------- Fetch Accounts ---------------- */

  const fetchAccounts = async () => {
    setLoading(true);
    setLoadError(false);

    try {
      const res = await fetch(`${BACKEND_URL}/accounts/middleman/list`, {
        credentials: "include",
        cache: "no-store",
      });

      if (!res.ok) throw new Error();

      const json = await res.json();

      const data = Array.isArray(json.middlemen) ? json.middlemen : [];

      if (!abortRef.current) {
        setAccounts(data);
        errorShownRef.current = false;
      }
    } catch {
      if (!abortRef.current) {
        setLoadError(true);

        if (!errorShownRef.current) {
          errorShownRef.current = true;
          toast({
            title: "Unable to load child accounts",
            description: "Please try again later.",
            status: "error",
            position: "top",
          });
        }
      }
    } finally {
      if (!abortRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    abortRef.current = false;
    fetchAccounts();
    return () => {
      abortRef.current = true;
    };
  }, []);

  /* ---------------- Update Status ---------------- */

  const updateStatus = async (accountId, accountStatus) => {
    try {
      const res = await fetch(`${BACKEND_URL}/accounts/middleman/${accountId}`, {
        method: "PUT",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ accountStatus }),
      });

      if (!res.ok) throw new Error();

      toast({
        title: "Status updated",
        status: "success",
        position: "top",
      });

      fetchAccounts();
    } catch {
      toast({
        title: "Failed to update status",
        status: "error",
        position: "top",
      });
    }
  };

  const handleStatusChangeClick = (accountId, newStatus) => {
    setPendingChange({ accountId, newStatus });
    onOpen();
  };

  const confirmStatusChange = () => {
    if (pendingChange) {
      updateStatus(pendingChange.accountId, pendingChange.newStatus);
    }
    onClose();
    setPendingChange(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "green";
      case "suspended":
        return "orange";
      case "revoked":
        return "red";
      default:
        return "gray";
    }
  };

  /* ---------------- Render ---------------- */

  return (
    <Flex justify="center" mt={12} mb={16} px={4}>
      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" width="100%" maxW="900px">
        <Heading size="md" mb={6}>
          Child Accounts
        </Heading>

        {loading ? (
          <Flex justify="center" py={10}>
            <Spinner />
          </Flex>
        ) : loadError ? (
          <Flex justify="center" py={10}>
            <Text color="gray.500">Unable to load child accounts.</Text>
          </Flex>
        ) : accounts.length === 0 ? (
          <Flex justify="center" py={10}>
            <Text color="gray.500">No child accounts yet.</Text>
          </Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {accounts.map((acc) => (
                <Tr key={acc._id}>
                  <Td>{acc.username}</Td>
                  <Td>{acc.role}</Td>
                  <Td>
                    <Flex align="center" gap={3}>
                      <Badge colorScheme={getStatusColor(acc.accountStatus)}>
                        {acc.accountStatus}
                      </Badge>

                      <Select
                        size="sm"
                        width="140px"
                        value={acc.accountStatus}
                        onChange={(e) => handleStatusChangeClick(acc._id, e.target.value)}
                        disabled={acc.accountStatus === "revoked"}
                      >
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="revoked">Revoked</option>
                      </Select>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      <Confirmation
        header="Change Account Status"
        body={`Are you sure you want to change this account's status to ${pendingChange?.newStatus}?`}
        isOpen={isOpen}
        handleClose={onClose}
        handleAction={confirmStatusChange}
        actionButtonText="Update Status"
        actionButtonBgColor="blue.500"
      />
    </Flex>
  );
}

export default IssuerMiddlemenList;
